import { useCallback, useMemo } from 'react';
import {
  Button,
  IconValidationValid,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { Dialog } from '@tamagui/dialog';

import { ModalContent } from '~/components/ModalContent';
import { ModalOverlay } from '~/components/ModalOverlay';
import { useAuthState } from '~/features/auth/useAuthState';
import { useModal } from '~/features/modals/useModal';
import { DOMAINS } from '~/features/navigation/domains';
import { useDomainName } from '~/features/navigation/useDomainName';
import { buildTestId } from '~/utils/buildTestId';

type AdminUserAddedModalProps = {
  testID: string;
  userId: string;
  userFullName: string;
  onViewUserProfile: (userId: string) => Promise<void>;
};

export const AdminUserAddedModal = ({
  testID,
  onViewUserProfile,
  userFullName,
  userId,
}: AdminUserAddedModalProps) => {
  const { closeModal } = useModal();
  const { userInfo } = useAuthState();
  const domain = useDomainName();

  const onClose = useCallback(() => {
    closeModal('AdminUserAdded');
  }, [closeModal]);

  const onOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const handleViewUserProfile = useCallback(() => {
    onClose();
    void onViewUserProfile(userId);
  }, [onViewUserProfile, userId, onClose]);

  const addedMessage = useMemo(() => {
    switch (domain) {
      case DOMAINS.ABBOTT_ADMINISTRATION:
        return `${userFullName} was added to LactateView.`;
      case DOMAINS.CUSTOMER_ADMINISTRATION:
        return `${userFullName} was added to the IDN.`;
      case DOMAINS.USER_ADMINISTRATION:
        return `${userFullName} was added to ${userInfo?.idnName ?? 'the IDN'}.`;
      default:
        return `${userFullName} was added.`;
    }
  }, [domain, userFullName, userInfo]);

  return (
    <Dialog open onOpenChange={onOpenChange} modal>
      <Dialog.Portal testID={buildTestId({ testID, suffix: 'portal' })}>
        <ModalOverlay testID={buildTestId({ testID, suffix: 'overlay' })} />

        <ModalContent
          testID={testID}
          key="content"
          paddingBottom="$12"
          maxWidth={800}
          minHeight={500}
          alignContent="center"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack alignItems="center">
            <IconValidationValid size="L" color="$support.positive.default" />
            <Text
              type="$h3.strong"
              testID={buildTestId({
                testID,
                suffix: 'title',
              })}
            >
              User Added
            </Text>

            <Text type="$bodySmall.default" color="$text.60" paddingTop="$3">
              {addedMessage}
            </Text>
          </Stack>
          <Stack>
            <Button
              variant="secondary"
              label="View User Profile"
              onPress={handleViewUserProfile}
              marginTop="$6"
              // @ts-expect-error Parallax doesn't type this prop
              minWidth={256}
              testID={buildTestId({
                testID,
                suffix: ['button', 'user', 'profile'],
              })}
            />

            <Button
              variant="primary"
              label="Done"
              onPress={onClose}
              marginTop="$2"
              // @ts-expect-error Parallax doesn't type this prop
              minWidth={256}
              testID={buildTestId({
                testID,
                suffix: ['button', 'done'],
              })}
            />
          </Stack>
        </ModalContent>
      </Dialog.Portal>
    </Dialog>
  );
};
